import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import colors from '../../../config/colors';

/**
 * EventItem - Single event row in the event feed
 * Shows event type, details, and timestamp
 *
 * Alert types from ESP32 pAlt field:
 * - dry_run: Pump running dry (low current)
 * - high_power / overload: Overload condition
 * - excessive_run / long_run: Pump running too long
 * - short_cycling: Pump cycling too frequently
 * - expected_run: Expected run didn't happen
 * - unusual_power: Unusual power pattern
 * - pump_started: Pump started running
 * - pump_finished: Pump finished running
 */
function EventItem({
    type = 'pump_finished',
    timestamp = '',
    duration = null,      // seconds
    power = null,         // watts
    interval = null,      // seconds since last run
    current = null,       // amps (for pump_finished)
    isAlert = false,
}) {
    const getIcon = () => {
        switch (type) {
            case 'dry_run':
                return { name: 'tint', color: '#ff6b6b' };
            case 'excessive_run':
            case 'long_run':
                return { name: 'exclamation-triangle', color: '#ff6b6b' };
            case 'high_power':
            case 'overload':
                return { name: 'exclamation-triangle', color: '#ff6b6b' };
            case 'short_cycling':
                return { name: 'refresh', color: '#ffa500' };
            case 'expected_run':
                return { name: 'clock-o', color: '#ffa500' };
            case 'unusual_power':
                return { name: 'bolt', color: '#ffa500' };
            case 'pump_started':
                return { name: 'play', color: '#33cc33' };
            case 'pump_finished':
                return { name: 'stop', color: '#888' };
            // Device system events (from Resets table)
            case 'device_boot':
                return { name: 'power-off', color: '#17a2b8' };
            case 'device_online':
                return { name: 'wifi', color: '#28a745' };
            case 'device_offline':
                return { name: 'wifi', color: '#dc3545' };
            case 'device_event':
            default:
                return { name: 'info-circle', color: '#6c757d' };
        }
    };

    const getTitle = () => {
        // For pump runs with alerts, show duration in title
        const durationSuffix = duration ? ` [${duration}s]` : '';
        switch (type) {
            case 'dry_run':
                return `Dry Run${durationSuffix}`;
            case 'excessive_run':
            case 'long_run':
                return `Excessive Run${durationSuffix}`;
            case 'high_power':
            case 'overload':
                return `High Power${durationSuffix}`;
            case 'short_cycling':
                return `Short Cycling${durationSuffix}`;
            case 'expected_run':
                return 'Expected Run Missing';
            case 'unusual_power':
                return 'Unusual Power';
            case 'pump_started':
                return 'Pump Started';
            case 'pump_finished':
                return `Pump Finished${durationSuffix}`;
            // Device system events (from Resets table)
            case 'device_boot':
                return 'Device Power On';
            case 'device_online':
                return 'Device Online';
            case 'device_offline':
                return 'Device Offline';
            case 'device_event':
                return 'System Event';
            default:
                return type;
        }
    };

    const formatInterval = (seconds) => {
        if (!seconds) return '';
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${hours}h ${mins}m`;
        }
        return `${minutes}m ${secs}s`;
    };

    const icon = getIcon();
    const showDetails = type === 'pump_finished' && (power || interval || current);

    // Determine if this is a warning/alert type
    const isWarning = ['dry_run', 'excessive_run', 'long_run', 'high_power', 'overload', 'short_cycling', 'expected_run', 'unusual_power'].includes(type);

    return (
        <View style={[
            styles.container,
            isAlert && styles.alertContainer,
            isWarning && !isAlert && styles.warningContainer
        ]}>
            <View style={styles.iconContainer}>
                <FontAwesome name={icon.name} size={16} color={icon.color} />
            </View>
            <View style={styles.contentContainer}>
                <Text style={styles.title}>{getTitle()}</Text>
                {showDetails && (
                    <Text style={styles.details}>
                        {power && `${power}w`}
                        {power && current && ' · '}
                        {current && `${current}A`}
                        {(power || current) && interval && ' · '}
                        {interval && `Interval ${formatInterval(interval)}`}
                    </Text>
                )}
            </View>
            <Text style={styles.timestamp}>{timestamp}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
        backgroundColor: colors.white,
    },
    alertContainer: {
        backgroundColor: '#f8d7da',
    },
    warningContainer: {
        backgroundColor: '#fff3cd',
    },
    iconContainer: {
        width: 32,
        alignItems: 'center',
    },
    contentContainer: {
        flex: 1,
        marginLeft: 8,
    },
    title: {
        fontSize: 15,
        fontWeight: '500',
        color: colors.dark,
    },
    details: {
        fontSize: 13,
        color: colors.medium,
        marginTop: 2,
    },
    timestamp: {
        fontSize: 13,
        color: colors.medium,
    },
});

export default EventItem;
